import React, { useState, useEffect } from 'react';
import { Shield, Clock, Filter, ExternalLink } from 'lucide-react';
import axios from 'axios';

const PoliceIncidents = () => {
  const [incidents, setIncidents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    const fetchIncidents = async () => {
      try {
        const response = await axios.get('/api/police-incidents', {
          params: {
            limit: 100,
            orderBy: 'occured_on',
            order: 'DESC'
          }
        });
        setIncidents(response.data);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching police incidents:', error);
        setLoading(false);
      }
    };

    fetchIncidents();
  }, []);

  const formatDateTime = (datetime) => {
    return new Date(datetime).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getIncidentStatus = (incident) => {
    const isRecent = new Date(incident.occured_on) > new Date(Date.now() - 24 * 60 * 60 * 1000);
    return isRecent ? 'Open or Active' : 'Closed';
  };

  const getStatusStyles = (status) => {
    return status === 'Open or Active'
      ? 'bg-yellow-50 text-yellow-600'
      : 'bg-green-50 text-green-600';
  };

  const getFilteredIncidents = () => {
    switch (filter) {
      case 'violent':
        return incidents.filter(incident => incident.violent_crime);
      case 'property':
        return incidents.filter(incident => incident.property_crime);
      default:
        return incidents;
    }
  };

  const getCategoryColor = (incident) => {
    if (incident.violent_crime) return 'bg-red-50 text-red-600';
    if (incident.property_crime) return 'bg-yellow-50 text-yellow-600';
    return 'bg-blue-50 text-blue-600';
  };

  const filteredIncidents = getFilteredIncidents();

  return (
    <div className="bg-white rounded-lg shadow-md p-6 border border-gray-100">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Shield className="h-5 w-5 text-blue-600" />
          <h3 className="text-lg font-semibold text-gray-900">Recent Police Incidents</h3>
        </div>
        <div className="flex items-center gap-2">
          <Filter className="h-4 w-4 text-gray-500" />
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="text-sm border border-gray-200 rounded px-2 py-1"
          >
            <option value="all">All Incidents</option>
            <option value="violent">Violent Crimes</option>
            <option value="property">Property Crimes</option>
          </select>
        </div>
      </div>
      
      {loading ? (
        <div className="animate-pulse space-y-4">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="h-16 bg-gray-100 rounded"></div>
          ))}
        </div>
      ) : (
        <div className="space-y-4 overflow-y-auto pr-2" style={{ 
          maxHeight: '410px',
          scrollbarWidth: 'thin',
          scrollbarColor: '#94A3B8 #E2E8F0'
        }}>
          {filteredIncidents.map((incident) => (
            <div 
              key={incident.objectid} 
              className="border border-gray-100 rounded-lg p-3 hover:bg-gray-50"
            >
              <div className="flex items-start justify-between">
                <div>
                  <h4 className="font-medium text-gray-900">{incident.ibr_category}</h4>
                  <p className="text-sm text-gray-600">{incident.ibr_offense_description}</p>
                </div>
                <span className="text-xs text-gray-500 flex items-center whitespace-nowrap ml-2">
                  <Clock size={12} className="mr-1 flex-shrink-0" />
                  {formatDateTime(incident.occured_on)}
                </span>
              </div>
              <div className="mt-2 flex items-center justify-between text-xs">
                <span className={`px-2 py-1 rounded ${getCategoryColor(incident)}`}>
                  {incident.neighborhood}
                </span>
                <span className={`px-2 py-1 rounded ${getStatusStyles(getIncidentStatus(incident))}`}>
                  {getIncidentStatus(incident)}
                </span>
              </div>
            </div>
          ))}
          {filteredIncidents.length === 0 && (
            <p className="text-gray-600 text-center py-8">
              No {filter !== 'all' ? `${filter} crimes` : 'incidents'} found.
            </p>
          )}
        </div>
      )}
      
      <div className="mt-4 space-y-2">
        <div className="text-xs text-gray-600 bg-gray-50 p-2 rounded-lg flex items-start gap-2">
          <div className="flex-shrink-0 mt-0.5">
            <ExternalLink size={14} />
          </div>
          <div>
            <span className="font-medium">Source:</span> San Diego Police Department NIBRS Crime Offenses.
            View more at{' '}
            <a 
              href="https://data.sandiego.gov/datasets/police-nibrs/" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-blue-600 hover:underline"
            >
              data.sandiego.gov
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PoliceIncidents;